import { baseApi } from "../baseApi";

const sanitizeBeneficiaryPayload = (payload = {}) => {
  const sanitized = { ...payload };

  if (sanitized?.old_file_no) {
    const val = Number(sanitized.old_file_no);
    if (val) {
      sanitized.old_file_no = val;
    } else {
      delete sanitized.old_file_no;
    }
  } else {
    delete sanitized?.old_file_no;
  }

  if (sanitized?.file_no) {
    const val = sanitized.file_no;
    if (val) {
      sanitized.file_no = val;
    } else {
      delete sanitized.file_no;
    }
  } else {
    delete sanitized?.file_no;
  }

  return sanitized;
};

export const beneficiaryApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      "Beneficiary",
      "PatientEMR",
      "ReferralForms",
      "SickLeaves",
      "AttendanceCertificate",
      "FitToTravelCertificate",
      "AccompanyCertificate",
      "FitToRejoinCertificate",
      "PatientVisits",
      "DepositHistory",
      "PatientCreditHistory",
      "PatientAuditHistory",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getBeneficiaries: builder.query({
        query: (payload) => ({
          url: "/v3/patient/getList",
          method: "POST",
          body: sanitizeBeneficiaryPayload(payload),
        }),
        providesTags: ["Beneficiary"],
      }),

      getPatientEMRReports: builder.query({
        query: (payload) => ({
          url: "/v3/emr/getReportByPatientId",
          method: "POST",
          body: payload,
        }),
      }),

      getPatientDoctorNotes: builder.query({
        query: (payload) => ({
          url: "/v3/emr/getDoctorNotesByPatientId",
          method: "POST",
          body: payload,
        }),
      }),

      getPatientEMRList: builder.query({
        query: (payload) => ({
          url: "/v3/emr/getByPatientId",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PatientEMR"],
      }),

      getReferralFormsList: builder.query({
        query: (payload) => ({
          url: "/v3/referral-form/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["ReferralForms"],
      }),

      getSickLeavesList: builder.query({
        query: (payload) => ({
          url: "/v3/leave/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["SickLeaves"],
      }),

      cancelSickLeave: builder.mutation({
        query: (payload) => ({
          url: "/v3/leave/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["SickLeaves"],
      }),

      getAttendanceCertificateList: builder.query({
        query: (payload) => ({
          url: "/v3/attendance-certificate/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["AttendanceCertificate"],
      }),

      getAttendanceCertificateById: builder.query({
        query: (payload) => ({
          url: "/v3/attendance-certificate/getById",
          method: "POST",
          body: payload,
        }),
      }),

      cancelAttendanceCertificate: builder.mutation({
        query: (payload) => ({
          url: "/v3/attendance-certificate/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["AttendanceCertificate"],
      }),

      getFitToTravelList: builder.query({
        query: (payload) => ({
          url: "/v3/fit-to-travel/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["FitToTravelCertificate"],
      }),

      getFitToTravelById: builder.query({
        query: (payload) => ({
          url: "/v3/fit-to-travel/getById",
          method: "POST",
          body: payload,
        }),
      }),

      cancelFitToTravel: builder.mutation({
        query: (payload) => ({
          url: "/v3/fit-to-travel/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["FitToTravelCertificate"],
      }),

      getAccompanyCertificateList: builder.query({
        query: (payload) => ({
          url: "/v3/accompany-certificate/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["AccompanyCertificate"],
      }),

      getAccompanyCertificateById: builder.query({
        query: (payload) => ({
          url: "/v3/accompany-certificate/getById",
          method: "POST",
          body: payload,
        }),
      }),

      cancelAccompanyCertificate: builder.mutation({
        query: (payload) => ({
          url: "/v3/accompany-certificate/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["AccompanyCertificate"],
      }),

      getFitToRejoinList: builder.query({
        query: (payload) => ({
          url: "/v3/fit-to-rejoin/getList",
          method: "POST",
          body: payload,
        }),
        providesTags: ["FitToRejoinCertificate"],
      }),

      getFitToRejoinById: builder.query({
        query: (payload) => ({
          url: "/v3/fit-to-rejoin/getById",
          method: "POST",
          body: payload,
        }),
      }),

      cancelFitToRejoin: builder.mutation({
        query: (payload) => ({
          url: "/v3/fit-to-rejoin/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["FitToRejoinCertificate"],
      }),

      getPatientVisits: builder.query({
        query: (payload) => ({
          url: "/v3/patient/visit/filter",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PatientVisits"],
      }),

      cancelReferralForm: builder.mutation({
        query: (payload) => ({
          url: "/v3/referral-form/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["ReferralForms"],
      }),

      getDepositHistory: builder.query({
        query: (payload) => ({
          url: "/v3/deposit/history/getPatinetHistory",
          method: "POST",
          body: payload,
        }),
        providesTags: ["DepositHistory"],
      }),

      getPatientCreditHistory: builder.query({
        query: (payload) => ({
          url: "/v3/patient/creditPayment/getCreditHistoryByPatientId",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PatientCreditHistory"],
      }),

      createPatient: builder.mutation({
        query: (payload) => ({
          url: "/v3/patient/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["Beneficiary"],
      }),

      updatePatient: builder.mutation({
        query: (payload) => ({
          url: "/v3/patient/update",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["Beneficiary"],
      }),

      deletePatient: builder.mutation({
        query: (payload) => ({
          url: "/v3/patient/delete",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["Beneficiary"],
      }),

      getPatientById: builder.query({
        query: (payload) => ({
          url: "/v3/patient/getById",
          method: "POST",
          body: payload,
        }),
      }),

      getPatientRecordSummary: builder.mutation({
        query: (payload) => ({
          url: "/v3/patient/recordSummary",
          method: "POST",
          body: payload,
        }),
      }),

      checkInsurance: builder.mutation({
        query: (payload) => ({
          url: "/v3/RCM/checkInsurance",
          method: "POST",
          body: payload,
        }),
      }),

      createSickLeave: builder.mutation({
        query: (payload) => ({
          url: "/v3/leave/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["SickLeaves"],
      }),

      updateSickLeave: builder.mutation({
        query: (payload) => ({
          url: "/v3/leave/update",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["SickLeaves"],
      }),

      createAttendanceCertificate: builder.mutation({
        query: (payload) => ({
          url: "/v3/attendance-certificate/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["AttendanceCertificate"],
      }),

      createFitToTravel: builder.mutation({
        query: (payload) => ({
          url: "/v3/fit-to-travel/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["FitToTravelCertificate"],
      }),

      createAccompanyCertificate: builder.mutation({
        query: (payload) => ({
          url: "/v3/accompany-certificate/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["AccompanyCertificate"],
      }),

      createFitToRejoin: builder.mutation({
        query: (payload) => ({
          url: "/v3/fit-to-rejoin/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["FitToRejoinCertificate"],
      }),

      createReferralForm: builder.mutation({
        query: (payload) => ({
          url: "/v3/referral-form/create",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["ReferralForms"],
      }),

      updateReferralForm: builder.mutation({
        query: (payload) => ({
          url: "/v3/referral-form/update",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["ReferralForms"],
      }),

      cancelPatientVisit: builder.mutation({
        query: (payload) => ({
          url: "/v3/visit/cancel",
          method: "POST",
          body: payload,
        }),
        invalidatesTags: ["PatientVisits"],
      }),

      getPatientAuditHistory: builder.query({
        query: (payload) => ({
          url: "/v3/patient/audit/history",
          method: "POST",
          body: payload,
        }),
        providesTags: ["PatientAuditHistory"],
      }),
    }),
  });

export const {
  useGetBeneficiariesQuery,
  useLazyGetBeneficiariesQuery,
  useGetPatientEMRReportsQuery,
  useLazyGetPatientEMRReportsQuery,
  useGetPatientDoctorNotesQuery,
  useLazyGetPatientDoctorNotesQuery,
  useGetPatientEMRListQuery,
  useLazyGetPatientEMRListQuery,
  useGetReferralFormsListQuery,
  useLazyGetReferralFormsListQuery,
  useCancelReferralFormMutation,
  useCreateReferralFormMutation,
  useUpdateReferralFormMutation,
  useGetSickLeavesListQuery,
  useLazyGetSickLeavesListQuery,
  useCancelSickLeaveMutation,
  useCreateSickLeaveMutation,
  useUpdateSickLeaveMutation,
  useGetAttendanceCertificateListQuery,
  useLazyGetAttendanceCertificateListQuery,
  useGetAttendanceCertificateByIdQuery,
  useLazyGetAttendanceCertificateByIdQuery,
  useCancelAttendanceCertificateMutation,
  useCreateAttendanceCertificateMutation,
  useGetFitToTravelListQuery,
  useLazyGetFitToTravelListQuery,
  useGetFitToTravelByIdQuery,
  useLazyGetFitToTravelByIdQuery,
  useCancelFitToTravelMutation,
  useCreateFitToTravelMutation,
  useGetAccompanyCertificateListQuery,
  useLazyGetAccompanyCertificateListQuery,
  useGetAccompanyCertificateByIdQuery,
  useLazyGetAccompanyCertificateByIdQuery,
  useCancelAccompanyCertificateMutation,
  useCreateAccompanyCertificateMutation,
  useGetFitToRejoinListQuery,
  useLazyGetFitToRejoinListQuery,
  useGetFitToRejoinByIdQuery,
  useLazyGetFitToRejoinByIdQuery,
  useCancelFitToRejoinMutation,
  useCreateFitToRejoinMutation,
  useGetPatientVisitsQuery,
  useLazyGetPatientVisitsQuery,
  useCancelPatientVisitMutation,
  useGetPatientAuditHistoryQuery,
  useLazyGetPatientAuditHistoryQuery,
  useGetDepositHistoryQuery,
  useLazyGetDepositHistoryQuery,
  useGetPatientCreditHistoryQuery,
  useLazyGetPatientCreditHistoryQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useDeletePatientMutation,
  useGetPatientByIdQuery,
  useLazyGetPatientByIdQuery,
  useGetPatientRecordSummaryMutation,
  useCheckInsuranceMutation,
} = beneficiaryApi;
